import { ContractError } from '@platform/contracts';
import { AuthFlowError } from "../auth/types.js";
/**
 * The one error shape screens deal with: an HTTP status when there was one, the contract's
 * machine code, and a sentence a person can act on. Never a vendor name, never a stack.
 */
export class AppError extends Error {
    kind;
    status;
    code;
    requestId;
    reason;
    name = 'AppError';
    constructor(kind, message, status = null, code = null, requestId = null, reason) {
        super(message);
        this.kind = kind;
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.reason = reason;
    }
}
const BY_CODE = {
    unauthenticated: 'Your session has ended. Sign in again.',
    forbidden: "You don't have access to do that.",
    notFound: "That doesn't exist, or you don't have access to it.",
    rateLimited: 'Too many requests. Wait a moment and try again.',
    internal: 'Something went wrong on our side. Try again in a moment.',
    unavailable: "This isn't available right now. Try again in a little while.",
};
function serverMessage(body) {
    if (typeof body !== 'object' || body === null)
        return undefined;
    const message = body.message;
    return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
}
/** `ContractError` / `AuthFlowError` / `TypeError` (fetch) → `AppError`. Idempotent. */
export function toAppError(e) {
    if (e instanceof AppError)
        return e;
    if (e instanceof ContractError) {
        if (e.kind === 'contract') {
            return new AppError('contract', 'Something went wrong on our side. Refresh the page and try again.', e.status, null, e.requestId ?? null, e);
        }
        const code = e.code ?? null;
        // Only validation and conflict messages are written for people; every other server message
        // may carry internal detail and is never shown (design standards DS-STATE-03, DS-CONTENT-02).
        const message = (code === 'validation' || code === 'conflict' ? serverMessage(e.body) : undefined) ??
            (e.status === 503 ? BY_CODE['unavailable'] : undefined) ??
            (code ? BY_CODE[code] : undefined) ??
            (e.status >= 500 ? BY_CODE['internal'] : 'That request could not be completed.');
        return new AppError('http', message ?? 'That request could not be completed.', e.status, code, e.requestId ?? null, e);
    }
    if (e instanceof AuthFlowError)
        return new AppError('auth', e.message, null, e.code, null, e);
    if (e instanceof TypeError && /fetch|network/i.test(e.message)) {
        return new AppError('network', "We couldn't connect. Check your internet connection and try again.", null, null, null, e);
    }
    return new AppError('unknown', 'Something went wrong. Try again.', null, null, null, e);
}
/** For toasts: the sentence to show for any failure. */
export function describeError(e, fallback) {
    const err = toAppError(e);
    return err.kind === 'unknown' && fallback ? fallback : err.message;
}
/** Field-level details from a `validation` error, keyed by JSON pointer (`/email` → `email`). */
export function fieldErrors(e) {
    const out = {};
    const err = toAppError(e);
    const cause = err.reason;
    if (!(cause instanceof ContractError) || typeof cause.body !== 'object' || cause.body === null)
        return out;
    const details = cause.body.details;
    if (!Array.isArray(details))
        return out;
    for (const d of details) {
        if (typeof d !== 'object' || d === null)
            continue;
        const { path, message } = d;
        if (typeof path === 'string' && typeof message === 'string')
            out[path.replace(/^\//, '').replace(/\//g, '.')] = message;
    }
    return out;
}
//# sourceMappingURL=errors.js.map